/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Bills Title component
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.1.0
 */
import { FC } from 'react';

import { useSelector } from 'react-redux';

import { EOnboardingComponentsId } from '@/main/modules/onboarding/types/onboardingTypes';
import { ELinkType } from '@components/common/InstructionLink/types';
import {
    IPageTitleSettingButtonProps,
    renderDropFilterButton,
    renderSettingButton,
} from '@components/common/PageTitle/PageTitle';
import { ITableField, TableFieldType } from '@components/Table/reducers/tableReducer';
import { TableNameType } from '@components/Table/types/tableTypes';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { InstructionLinkDivStyled } from '@pages/PageBills/components/BillsTitle/BillsTitileStyled';
import { stringFormat } from '@utils/stringFormat';

import { icons } from '../../../../assets';
import { InstructionLink, PageTitle } from '../../../../components/common';
import TableSetting from '../../../../components/TableSetting/TableSetting';
import DensityButton from '../../../../modules/density/components/DensityButton';
import ExportButton from '../../../../modules/exports/components/ExportButton';
import { IPageBillsTranslation } from '../../../../modules/internationalization/types';
import { BillsField, MapPageBillsTypeToBillsTypeTranslation, PageBillsType } from '../../pageBillsTypes';


interface IBillsTitleProps {
    billsType: PageBillsType;
    billsExportMaxCount?: number;
    counter: number;
    dataName?: string;
    disableExport: boolean;
    exportPath: string;
    isFilterSelected: boolean;
    onDropFiltersClick: () => void;
    openModal: (payload: IModalOpenPayload) => void;
    sendTableFields: (tableFields: Array<ITableField<BillsField>>) => void;
    tableFields: Array<ITableField<BillsField>>;
    translation: IPageBillsTranslation;
    hasNewBills?: boolean;
    isFastActionsPage?: boolean;
}

const BillsTitle: FC<IBillsTitleProps> = (props) => {
    const { counter, isFilterSelected, onDropFiltersClick, tableFields, translation, isFastActionsPage } = props;

    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));
    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { billsHowto, billsExport } } = translationCommon;

    const pageTitleDescription =
        !props?.hasNewBills ? translation.title.helperText : undefined;

    const onOpenTableSetting = (): void => {
        props.openModal({
            render: (props: IModalRenderProps) => (
                <TableSetting
                    fields={filterFields(tableFields, ['S', 'M'])}
                    isOpen={false}
                    locale={{
                        title: translation.title.tableSetting,
                        table: translation.table,
                    }}
                    requiredFields={filterFields(tableFields, ['C'])}
                    onApply={(tableFields: Array<ITableField<BillsField>>) => {
                        onSendTableFields(tableFields);
                        props.onCloseModal();
                    }}
                    onClose={props.onCloseModal}
                />
            ),
        });
    };

    const onSendTableFields = (tableFields: Array<ITableField<BillsField>>): void => {
        props.sendTableFields(tableFields);
    };

    /**
     * @description Filter table fields according to field type.
     * @param {ITableField<BillsField>[]} tableFields
     * @param {TableFieldType[]} type
     * @return {ITableField<BillsField>[]}
     */
    const filterFields = (
        tableFields: Array<ITableField<BillsField>>,
        type: TableFieldType[],
    ): Array<ITableField<BillsField>> =>
        tableFields.filter((value: ITableField<BillsField>) => type.includes(value.type));

    /**
     * @description Return array of table setting buttons
     * @return {IPageTitleSettingButtonProps[]}
     */
    const tableSettingButtons = (): IPageTitleSettingButtonProps[] => [
        {
            id: 0,
            iconLeft: icons.settingIcon,
            onClick: onOpenTableSetting,
            value: translation.title.tableSetting.titleBtn,
            dataName: 'Settings of columns',
        },
        {
            id: 1,
            component: (
                <ExportButton
                    disabled={props.disableExport}
                    disableMessage={stringFormat(props.translation.title.disableExport, [props.billsExportMaxCount])}
                    instructionLink={billsExport}
                    src={props.exportPath}
                    tableName={TableNameType.bills}
                />
            ),
        },
        {
            id: 4,
            component: <DensityButton page={ECommonTranslationPages.bills} />,
        },
    ];

    return (
        <PageTitle
            count={counter}
            dataName="bills"
            description={pageTitleDescription}
            subTitle={
                isFastActionsPage
                    ? undefined
                    : translation.title.subTitles[MapPageBillsTypeToBillsTypeTranslation[props.billsType]]
            }
            subTitleOnboarding={EOnboardingComponentsId.billsDescription}
            title={translation.title.header}
        >
            {isInstructionsLinksEnabled && billsHowto.instructionLink &&
                <InstructionLinkDivStyled>
                    <InstructionLink {...{ ...billsHowto, linkType: ELinkType.TEXT }}/>
                </InstructionLinkDivStyled>
            }
            {
                // render drop filter button
                isFilterSelected &&
                    renderDropFilterButton({
                        onClick: onDropFiltersClick,
                        value: translation.title.dropFilters,
                    })
            }
            {
                // render table setting buttons in right side of title
                tableSettingButtons().map((button) => {
                    if (isFastActionsPage && button.id === 1) {
                        return <></>;
                    }
                    if (button.component) {
                        return (
                            <div className="page-title__button" key={button.id}>
                                {button.component}
                            </div>
                        );
                    }
                    return renderSettingButton(button);
                })
            }
        </PageTitle>
    );
};

export default BillsTitle as FC<any>;
